import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import AuthRequiredModal from '../components/AuthRequiredModal';
import { AUTH_REQUIRED_MESSAGE, isCustomerAuthenticated } from '../utils/customerAuth';
import { savePendingAuthAction, type PendingAuthAction } from '../utils/pendingAuthAction';

type AuthGateContextType = {
  isCustomerAuthenticated: boolean;
  openAuthModal: (opts?: { message?: string; sessionExpired?: boolean; returnPath?: string }) => void;
  closeAuthModal: () => void;
  requireCustomerAuth: (action: PendingAuthAction, onAuthed: () => void | Promise<void>) => Promise<boolean>;
};

const AuthGateContext = createContext<AuthGateContextType | undefined>(undefined);

export const AuthGateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState(AUTH_REQUIRED_MESSAGE);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [returnPath, setReturnPath] = useState<string | undefined>();
  const [customerAuthed, setCustomerAuthed] = useState(isCustomerAuthenticated);

  const refreshCustomerAuth = useCallback(() => {
    setCustomerAuthed(isCustomerAuthenticated());
  }, []);

  useEffect(() => {
    refreshCustomerAuth();
    const onAuthChange = () => refreshCustomerAuth();
    const onSessionExpired = () => {
      setModalMessage('Your session has expired. Please sign in again to continue.');
      setSessionExpired(true);
      setReturnPath(window.location.pathname + window.location.search);
      setModalOpen(true);
    };
    window.addEventListener('nls-auth-changed', onAuthChange);
    window.addEventListener('storage', onAuthChange);
    window.addEventListener('nls-session-expired', onSessionExpired);
    return () => {
      window.removeEventListener('nls-auth-changed', onAuthChange);
      window.removeEventListener('storage', onAuthChange);
      window.removeEventListener('nls-session-expired', onSessionExpired);
    };
  }, [refreshCustomerAuth]);

  const openAuthModal = useCallback(
    (opts?: { message?: string; sessionExpired?: boolean; returnPath?: string }) => {
      setModalMessage(opts?.message || AUTH_REQUIRED_MESSAGE);
      setSessionExpired(!!opts?.sessionExpired);
      setReturnPath(opts?.returnPath || window.location.pathname + window.location.search);
      setModalOpen(true);
    },
    []
  );

  const closeAuthModal = useCallback(() => {
    setModalOpen(false);
    setSessionExpired(false);
  }, []);

  const requireCustomerAuth = useCallback(
    async (action: PendingAuthAction, onAuthed: () => void | Promise<void>) => {
      if (isCustomerAuthenticated()) {
        await onAuthed();
        return true;
      }
      savePendingAuthAction(action);
      openAuthModal({ returnPath: window.location.pathname + window.location.search });
      return false;
    },
    [openAuthModal]
  );

  return (
    <AuthGateContext.Provider
      value={{
        isCustomerAuthenticated: customerAuthed,
        openAuthModal,
        closeAuthModal,
        requireCustomerAuth,
      }}
    >
      {children}
      <AuthRequiredModal
        open={modalOpen}
        onClose={closeAuthModal}
        message={modalMessage}
        sessionExpired={sessionExpired}
        returnPath={returnPath}
      />
    </AuthGateContext.Provider>
  );
};

export const useAuthGate = () => {
  const ctx = useContext(AuthGateContext);
  if (!ctx) {
    throw new Error('useAuthGate must be used within AuthGateProvider');
  }
  return ctx;
};
